// src/components/Dialogs/OrganizationTemplateTab.jsx

import React, { useRef, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Button
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';

import { useNavigate }   from 'react-router-dom';
import FormCard from '../Cards/FormCard';
// 1) Import your duplication hook
import { useDuplicateForm } from '../../hooks/useDuplicateForm';
// 2) Import the success modal
import DuplicateSuccessModal from './DuplicateSuccessModal';

export default function OrganizationTemplateTab({
  orgTemplates = [],
  isLoading = false,
  hasMore = false,
  fetchNextBatch,
  canPublish = false,
  onSubmitTemplateClick,
  selectedDoc,
  onSelectDoc,
  onUnpublishSuccess,
}) {
  // Logging input props on every render
//  console.log('[OrganizationTemplateTab] Rendering with props:', {
//    orgTemplatesLength: orgTemplates.length,
//    isLoading,
//    hasMore,
//    canPublish,
//    selectedDoc,
//  });

  // We'll track if we've done our *first* successful load or final check
  // (i.e. we either got some templates or confirmed none remain)
  const [hasDoneFirstLoad, setHasDoneFirstLoad] = useState(false);

  const sentinelRef = useRef(null);
  const navigate     = useNavigate();

  // IntersectionObserver for infinite scrolling
  useEffect(() => {
//    console.log('[OrganizationTemplateTab] useEffect for IntersectionObserver:', {
//      sentinelRefExists: !!sentinelRef.current,
//      hasMore,
//      isLoading,
//      fetchNextBatchExists: !!fetchNextBatch,
//    });

    if (!sentinelRef.current) {
//      console.log('[OrganizationTemplateTab] sentinelRef is not set yet, returning early.');
      return;
    }
    if (!hasMore) {
//      console.log('[OrganizationTemplateTab] hasMore is false, no need to observe intersection.');
      return;
    }
    if (!fetchNextBatch) {
//      console.log('[OrganizationTemplateTab] fetchNextBatch is not provided, returning early.');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      // Log intersection each time it triggers
//      console.log('[OrganizationTemplateTab] IntersectionObserver entry:', { isIntersecting: entry.isIntersecting });

      if (entry.isIntersecting && !isLoading) {
//        console.log('[OrganizationTemplateTab] Entry is intersecting and not loading. Fetching next batch.');
        fetchNextBatch();
      }
    }, { rootMargin: '200px' });

    observer.observe(sentinelRef.current);

    return () => {
//      console.log('[OrganizationTemplateTab] Cleaning up IntersectionObserver.');
      observer.disconnect();
    };
  }, [hasMore, isLoading, fetchNextBatch]);

  // Once loading is finished, if we have templates OR know there are no more,
  // we've effectively completed the "first load" of data.
  useEffect(() => {
//    console.log('[OrganizationTemplateTab] Checking if first load is complete:', {
//      isLoading,
//      orgTemplatesLength: orgTemplates.length,
//      hasMore,
 //     hasDoneFirstLoad,
 //   });

    // Only mark first load complete if we haven't already
    if (!hasDoneFirstLoad) {
      // If not loading AND (got at least 1 template OR confirmed there's none left)
      if (!isLoading && (orgTemplates.length > 0 || !hasMore)) {
//        console.log('[OrganizationTemplateTab] Marking first load as complete.');
        setHasDoneFirstLoad(true);
      }
    }
  }, [isLoading, orgTemplates, hasMore, hasDoneFirstLoad]);

  // Group the templates by category
  const groupedCategories = useMemo(() => {
//    console.log('[OrganizationTemplateTab] useMemo grouping orgTemplates:', orgTemplates);
    if (!orgTemplates.length) {
//      console.log('[OrganizationTemplateTab] orgTemplates is empty, returning [].');
      return [];
    }

    const categoryMap = new Map();
    for (const tpl of orgTemplates) {
      const cat = tpl.templateCategory || 'Uncategorized';
      if (!categoryMap.has(cat)) {
        categoryMap.set(cat, []);
      }
      categoryMap.get(cat).push(tpl);
    }

    const categories = Array.from(categoryMap.keys()).sort((a, b) => a.localeCompare(b));
//    console.log('[OrganizationTemplateTab] Sorted categories:', categories);

    return categories.map((cat) => ({
      category: cat,
      forms: categoryMap.get(cat),
    }));
  }, [orgTemplates]);



//  console.log('[OrganizationTemplateTab] Computed values:', {
//    totalTemplates,
//    showZeroState,
//    isLoading,
//    hasDoneFirstLoad,
//    allLoading
//  });

  // Use the duplication hook here
  const {
    handleDuplicateForm,
    duplicateModalState,
    dismissDuplicateModal,
    goToDuplicatedForm,
  } = useDuplicateForm();

  // 1️⃣  “Configure” → open Builder directly (NO duplication, NO modal)
  const onConfigureTemplate = (tplObj) => {
    if (!tplObj?.id) return;
    navigate(`/form-builder/${tplObj.id}/design`, {
   state: { formId: tplObj.id, editingTemplate: true }
    });
  };

  // 2️⃣  “Duplicate” (More ⋮ menu) → explicit copy + modal
  const onDuplicateTemplate = (tplObj) => {
    if (!tplObj?.id) return;
    handleDuplicateForm(tplObj.id);
  };
  // Now we can break down the render:
  // 1) If we're allLoading, show a spinner if desired.
  // 2) If not allLoading but zero templates => show zero state.
  // 3) Otherwise => show the grouped templates.

  /* ------------------------------------------------------------------ */
  /*  render                                                            */
  /* ------------------------------------------------------------------ */
  const totalTemplates = orgTemplates.length;
  const allLoading     = isLoading || !hasDoneFirstLoad;
  const showZeroState  = !allLoading && totalTemplates === 0;

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: 8,
        py: 3,
        width: '100%',
      }}
    >
      {console.log('[OrganizationTemplateTab] Rendering top bar with totalTemplates:', totalTemplates, 'canPublish:', canPublish)}

      {totalTemplates > 0 && canPublish && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mb: 0
          }}
        >
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={onSubmitTemplateClick}
            sx={{
              textTransform: 'none',
              minWidth: 160,
              backgroundColor: '#fff',
              boxShadow: (theme) => theme.shadows[2],
            }}
          >
            Submit template
          </Button>
        </Box>
      )}

      {/* 
        1) If we're loading or haven't finished the first load => spinner 
           (only if you want a spinner during the entire first load)
      */}
      {allLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, minHeight: '300px' }}>
          {console.log('[OrganizationTemplateTab] allLoading is true => showing spinner.')}
          <CircularProgress size={32} />
        </Box>
      )}

      {/* 2) If not loading, we've done the first load, and have no templates => zero state */}
      {showZeroState && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 48px)',
            textAlign: 'center',
          }}
        >
          {console.log('[OrganizationTemplateTab] Showing zero-state UI')}
          <DescriptionIcon
            sx={{
              fontSize: 80,
              color: 'grey.500',
              mb: 3,
            }}
          />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 'medium' }}>
            No templates available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your organization doesn&apos;t have any templates yet. Be the first to submit one.
          </Typography>

          {canPublish && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={onSubmitTemplateClick}
              sx={{
                textTransform: 'none',
                minWidth: 160,
                backgroundColor: '#fff',
                boxShadow: (theme) => theme.shadows[2],
              }}
            >
              Submit template
            </Button>
          )}
        </Box>
      )}

      {/* 3) If not allLoading and we have templates => show them */}
      {!allLoading && totalTemplates > 0 && (
        <>
          {groupedCategories.map(({ category, forms }) => {
            console.log('[OrganizationTemplateTab] Rendering category:', category, 'with', forms.length, 'forms');
            return (
              <Box key={category} sx={{ mb: 4 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 'medium',
                    color: 'text.secondary'
                  }}
                >
                  {category}
                </Typography>

                <Grid
                  container
                  spacing={4}
                  wrap="nowrap"
                  sx={{ overflowX: 'auto' }}
                >
                  {forms.map((tplObj) => {
                    const docId = tplObj.id;
                    const isSelected = selectedDoc?.id === docId;

//                    console.log('[OrganizationTemplateTab] Rendering FormCard for docId:', docId, 'isSelected:', isSelected);

                    return (
                      <Grid
                        item
                        key={docId}
                        sx={{ flex: '0 0 auto' }}
                      >
                        <FormCard
                          form={tplObj}
                          onClick={() => onSelectDoc?.(tplObj)}  // if you want to highlight or select
                          isSelected={isSelected}
                          showOverflowMenu
                          isTemplateInGallery
                          templateScope="global"
                          onConfigureTemplate={onConfigureTemplate}
                          onDuplicateTemplate={onDuplicateTemplate}
                          onUnpublishSuccess={onUnpublishSuccess}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </>
      )}

      {/* If loading after first load, we can display a smaller spinner for infinite scroll. */}
      {!allLoading && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          {console.log('[OrganizationTemplateTab] Showing smaller loading spinner for subsequent loads.')}
          <CircularProgress size={24} />
        </Box>
      )}

      <Box ref={sentinelRef} sx={{ height: '1px', width: '100%', mt: 2 }} />

      {console.log('[OrganizationTemplateTab] DuplicateSuccessModal state:', duplicateModalState)}
      <DuplicateSuccessModal
        open={duplicateModalState.open}
        onClose={dismissDuplicateModal}
        onNavigate={goToDuplicatedForm}
      />
    </Box>
  );
}
